import { useCallback, useRef } from 'react';

import uniq from 'lodash/uniq';
import { di } from 'react-magnetic-di';
import { useRelayEnvironment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	type LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
	LOOKUP_SOURCE_PROJECTS_ASYNC_DATA,
	LOOKUP_SOURCE_USERS_ASYNC_DATA,
} from '../../../common/constants';
import type { FieldMapFromIframe } from '../../../common/types/field';
import type { LookupData, LookupProjects, LookupUsers } from '../../../common/types/lookup';
import { isNotAutomaticId } from '../../../common/utils/is-not-automatic-id';
import { convertProjectIdsToAris } from '../../../common/utils/project-ari';
import { fetchProjects } from '../../../services/fetch-projects';
import { fetchUsers } from '../../../services/fetch-users';
import { useFormMetadata } from '../../issue-adjustments/hooks/use-form-metadata';
import { useViewType } from '../../issue-adjustments/hooks/use-view-type';
import { useTenantContext } from '../../tenant-context';
import { getSupportedFieldConfiguration } from '../../view-configuration';

import type { AsyncFieldsValues } from './types';

export const useLookupFieldData = () => {
	di(
		useRelayEnvironment,
		useFormMetadata,
		fetchUsers,
		fetchProjects,
		useViewType,
		useTenantContext,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useTenantContext();
	const environment = useRelayEnvironment();

	const formMetadata = useFormMetadata();
	const formMetadataRef = useRef(formMetadata);
	const viewType = useViewType();

	/**
	 * We want to have an up-to-date data when this hook's
	 * returned function is being called in useSubmit callback function
	 * thats why we keep it in a ref object
	 */
	formMetadataRef.current = formMetadata;

	return useCallback(
		async (
			fieldsChanges: FieldMapFromIframe,
		): Promise<Exclude<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>> => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const lookupData = {} as Exclude<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>;

			const asyncFieldsValues = Object.entries(fieldsChanges).reduce(
				(acc: AsyncFieldsValues, [fieldId, fieldData]) => {
					if (!formMetadataRef.current[fieldId]) {
						return acc;
					}

					const { fieldType } = formMetadataRef.current[fieldId];
					const fieldValue = fieldData.value;
					const supportedFieldsConfiguration = getSupportedFieldConfiguration(viewType, fieldType);

					const lookupSource = supportedFieldsConfiguration?.lookupSource;
					const isUserLookupField = lookupSource === LOOKUP_SOURCE_USERS_ASYNC_DATA;
					const isProjectLookupField = lookupSource === LOOKUP_SOURCE_PROJECTS_ASYNC_DATA;

					if (isUserLookupField && fieldValue && isNotAutomaticId(fieldValue)) {
						const currentAccountIds = acc.accountIds ? [...acc.accountIds] : [];
						const newAccountIds = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
						acc.accountIds = [...currentAccountIds, ...newAccountIds];
					}

					if (isProjectLookupField && fieldValue) {
						const currentProjectIds = acc.projectIds ? [...acc.projectIds] : [];
						const newProjectIds = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
						acc.projectIds = [...currentProjectIds, ...newProjectIds];
					}
					/** Add extra checks to add other lookup type e.g. issues lookup */

					return acc;
				},
				{},
			);
			let users: LookupUsers = [];
			let projects: LookupProjects = [];

			if (asyncFieldsValues.accountIds) {
				const uniqueAccountIds = uniq(asyncFieldsValues.accountIds);
				users = await fetchUsers(environment, uniqueAccountIds, createAnalyticsEvent);
			}

			if (asyncFieldsValues.projectIds) {
				const uniqueProjectIds = uniq(asyncFieldsValues.projectIds);

				// Convert numeric project IDs to ARIs before fetching
				// GraphQL expects ARIs like "ari:cloud:jira:{cloudId}:project/10001", not plain "10001"
				const projectAris = convertProjectIdsToAris(uniqueProjectIds, cloudId);
				projects = await fetchProjects(environment, projectAris, createAnalyticsEvent);
			}

			lookupData[LOOKUP_SOURCE_USERS_ASYNC_DATA] = users;
			lookupData[LOOKUP_SOURCE_PROJECTS_ASYNC_DATA] = projects;

			return lookupData;
		},
		[createAnalyticsEvent, viewType, environment, cloudId],
	);
};
